import React, {useEffect, useState} from "react";
import { Card, Row, Empty,  Tag, Col, Space, Layout, Divider, Typography } from "antd";
import NivoChordChart from "../../charts/nivo/NivoChordChart";
import {
    extractEtfHoldingsLinks,
    transformDataForChord,
    findTopOverlappedTickers,
    computeOverlapMatrix,
    transformMatrixToHeatmapData,
    findEtfWithMostConnections,
    findEtfWithMostUnique
} from "./etfChordUtils";
import {useTickerSearchIndex} from "../../modules/Search/useTickerSearchIndex";
import TickerChip from "../TickerChip";
import {useSelector} from "react-redux";
import {getTickersBySymbols} from "../../store/slices/globalResources.slice";
import isEqual from 'lodash/isEqual';
import {useDeepMemo} from "../../hooks/useDeepMemo";


/**
 *
 * @param parents {Object<string, {holdings: Array<{symbol: string, wgt: number}>}>} - An object with ETF names as
 *          ....keys and an array of holdings as values
 * @returns {JSX.Element}
 * @constructor
 */
const EtfChord = ({ parents }) => {
  const [topTickers, setTopTickers] = useState([]);
  const [heatData, setHeatData] = useState([]);
  const [mostUnique, setMostUnique] = useState(null);
  const [mostConnected, setMostConnected] = useState(null);
  const [stockLimit, setStockLimit] = useState(30);


  const memoizedParents = useDeepMemo(parents, [parents]);

  const { tickers, loading } = useTickerSearchIndex(topTickers); // Updated to use topTickers
  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });


  useEffect(() => {
    const links = extractEtfHoldingsLinks(memoizedParents, stockLimit);
    const matrix = computeOverlapMatrix(links);
    const keys = Object.keys(memoizedParents);
    const newHeatmapData = transformMatrixToHeatmapData(matrix, keys);
    const newTopTickers = findTopOverlappedTickers(links, 10).map(x => x.ticker);
    const newMostUnique = findEtfWithMostUnique(links);
    const newMostConnected = findEtfWithMostConnections(links);

    if (!isEqual(heatData, newHeatmapData)) setHeatData(newHeatmapData);
    if (!isEqual(topTickers, newTopTickers)) setTopTickers(newTopTickers);
    if (!isEqual(mostUnique, newMostUnique)) setMostUnique(newMostUnique);
    if (!isEqual(mostConnected, newMostConnected)) setMostConnected(newMostConnected);
  }, [memoizedParents, stockLimit]); // Only re-run effect if parents actually change

    useEffect(() => {
        const handleResize = () => {
            setChartDimensions({
                width: window.innerWidth * 0.7,
                height: window.innerHeight * 0.7
            });
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);


  if (loading) {
    return <Empty />;
  }

  const chordData = parents ? transformDataForChord(extractEtfHoldingsLinks(parents, stockLimit)) : null;



  return (
    <Layout style={{padding: '2.5%'}}>
        <Layout.Sider width={300} style={{background: "#fff"}} className={'px-3'}>
            <Space direction={'vertical'} >
                <Typography.Text className={'mb-2'} strong style={{fontWeight: '700'}}>Most Represented</Typography.Text>
                {tickers.map((ticker) => (
                    <div className={'dropped-shadow my-3'} style={{borderRadius: '25px'}}>
                        <TickerChip key={ticker} ticker={ticker}/>
                    </div>
                ))}
            </Space>
        </Layout.Sider>
        <Layout.Content style={{padding: '20px'}}>
                <div style={{height: `${chartDimensions.height}px`, width: `${chartDimensions.width}px`, margin: 'auto'}}>
                    <NivoChordChart
                        {...chordData}
                        legend={false}
                        height={chartDimensions.height}
                    />
                </div>
        </Layout.Content>
    </Layout>
  );
};
export default EtfChord;